import React, { useEffect, KeyboardEvent as ReactKeyboardEvent, useState } from 'react';
import { Editor } from 'slate';
import { Avatar, Icon, Icons, MenuItem, Text } from 'folds';
import { Room, RoomMember } from 'matrix-js-sdk';

import { AutocompleteQuery } from './autocompleteQuery';
import { AutocompleteMenu } from './AutocompleteMenu';
import { useActiveAutocompleteItemScroll } from './useActiveAutocompleteItemScroll';
import { useRoomMembers } from '../../../hooks/useRoomMembers';
import { useMatrixClient } from '../../../hooks/useMatrixClient';
import {
  SearchItemStrGetter,
  UseAsyncSearchOptions,
  useAsyncSearch,
} from '../../../hooks/useAsyncSearch';
import { onAutocompleteItemKeyDown, onAutocompleteNavigation } from '../../../utils/keyboard';
import { createMentionElement, moveCursor, replaceWithElement } from '../utils';
import { useKeyDown } from '../../../hooks/useKeyDown';
import { getMxIdLocalPart } from '../../../utils/matrix';
import { getMemberDisplayName, getMemberSearchStr } from '../../../utils/room';
import { UserAvatar } from '../../user-avatar';
import { useMediaAuthentication } from '../../../hooks/useMediaAuthentication';
import { Membership } from '../../../../types/matrix/room';
import {
  getRecentUserMentionIds,
  putRecentUserMention,
  sortByRecentUserIds,
} from './recentUserMentions';

type MentionAutoCompleteHandler = (userId: string, name: string) => void;

function UnknownMentionItem({
  userId,
  name,
  handleAutocomplete,
  selected,
  onMouseEnter,
  itemRef,
}: {
  userId: string;
  name: string;
  handleAutocomplete: MentionAutoCompleteHandler;
  selected: boolean;
  onMouseEnter: () => void;
  itemRef: (element: HTMLButtonElement | null) => void;
}) {
  return (
    <MenuItem
      as="button"
      ref={itemRef}
      radii="300"
      aria-selected={selected}
      onMouseEnter={onMouseEnter}
      onKeyDown={(evt: ReactKeyboardEvent<HTMLButtonElement>) =>
        onAutocompleteItemKeyDown(evt, () => handleAutocomplete(userId, name))
      }
      onClick={() => handleAutocomplete(userId, name)}
      before={
        <Avatar size="200">
          <UserAvatar
            userId={userId}
            renderFallback={() => <Icon size="50" src={Icons.User} filled />}
          />
        </Avatar>
      }
    >
      <Text style={{ flexGrow: 1 }} size="B400">
        {name}
      </Text>
    </MenuItem>
  );
}

type UserMentionAutocompleteProps = {
  room: Room;
  editor: Editor;
  query: AutocompleteQuery<string>;
  requestClose: () => void;
};

const withAllowedMembership = (member: RoomMember): boolean =>
  member.membership === Membership.Join ||
  member.membership === Membership.Invite ||
  member.membership === Membership.Knock;

const SEARCH_OPTIONS: UseAsyncSearchOptions = {
  limit: 1000,
  matchOptions: {
    contain: true,
  },
};

const mxIdToName = (mxId: string) => getMxIdLocalPart(mxId) ?? mxId;
const getRoomMemberStr: SearchItemStrGetter<RoomMember> = (m, query) =>
  getMemberSearchStr(m, query, mxIdToName);

export function UserMentionAutocomplete({
  room,
  editor,
  query,
  requestClose,
}: UserMentionAutocompleteProps) {
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const roomId: string = room.roomId!;
  const roomAliasOrId = room.getCanonicalAlias() || roomId;
  const members = useRoomMembers(mx, roomId);

  const [result, search, resetSearch] = useAsyncSearch(members, getRoomMemberStr, SEARCH_OPTIONS);
  const currentUserId = mx.getUserId();
  const [recentUserIds, setRecentUserIds] = useState(() =>
    getRecentUserMentionIds(currentUserId, roomId),
  );
  const autoCompleteMembers = sortByRecentUserIds(
    (result ? result.items : members).filter(
      (m) => withAllowedMembership(m) && m.userId !== currentUserId,
    ),
    recentUserIds,
    (m) => m.userId,
  ).slice(0, 20);
  const showRoomMention = query.text === 'room';
  const itemCount = autoCompleteMembers.length + (showRoomMention ? 1 : 0);
  const [activeIndex, setActiveIndex] = useState(0);
  const registerItemRef = useActiveAutocompleteItemScroll<HTMLButtonElement>(
    activeIndex,
    itemCount,
  );

  useEffect(() => {
    if (query.text) search(query.text);
    else resetSearch();
  }, [query.text, search, resetSearch]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query.text, itemCount]);

  useEffect(() => {
    setRecentUserIds(getRecentUserMentionIds(currentUserId, roomId));
  }, [currentUserId, roomId]);

  const handleAutocomplete = (uId: string, name: string, rememberRecentUser = true) => {
    if (rememberRecentUser) {
      setRecentUserIds(putRecentUserMention(currentUserId, roomId, uId));
    }

    const mentionEl = createMentionElement(
      uId,
      name.startsWith('@') ? name : `@${name}`,
      mx.getUserId() === uId || roomAliasOrId === uId,
    );
    replaceWithElement(editor, query.range, mentionEl);
    moveCursor(editor, true);
    requestClose();
  };

  useKeyDown(
    window,
    (evt: KeyboardEvent) => {
      onAutocompleteNavigation(evt, itemCount, activeIndex, setActiveIndex, () => {
        if (showRoomMention && activeIndex === 0) {
          handleAutocomplete(roomAliasOrId, '@room', false);
          return;
        }
        const roomMember = autoCompleteMembers[showRoomMention ? activeIndex - 1 : activeIndex];
        if (roomMember) handleAutocomplete(roomMember.userId, roomMember.name);
      });
    },
    true,
  );

  const getName = (member: RoomMember) =>
    getMemberDisplayName(room, member.userId) ?? getMxIdLocalPart(member.userId) ?? member.userId;

  // TODO: support @room
  if (itemCount === 0) {
    return null;
  }

  return (
    <AutocompleteMenu headerContent={<Text size="L400">Mentions</Text>} requestClose={requestClose}>
      {showRoomMention && (
        <UnknownMentionItem
          userId={roomAliasOrId}
          name="@room"
          handleAutocomplete={handleAutocomplete}
          selected={activeIndex === 0}
          onMouseEnter={() => setActiveIndex(0)}
          itemRef={registerItemRef(0)}
        />
      )}
      {autoCompleteMembers.map((roomMember, memberIndex) => {
        const index = showRoomMention ? memberIndex + 1 : memberIndex;
        const avatarMxcUrl = roomMember.getMxcAvatarUrl();
        const avatarUrl = avatarMxcUrl
          ? mx.mxcUrlToHttp(avatarMxcUrl, 32, 32, 'crop', undefined, false, useAuthentication)
          : undefined;
        return (
          <MenuItem
            key={roomMember.userId}
            as="button"
            ref={registerItemRef(index)}
            radii="300"
            aria-selected={activeIndex === index}
            onMouseEnter={() => setActiveIndex(index)}
            onKeyDown={(evt: ReactKeyboardEvent<HTMLButtonElement>) =>
              onAutocompleteItemKeyDown(evt, () =>
                handleAutocomplete(roomMember.userId, getName(roomMember)),
              )
            }
            onClick={() => handleAutocomplete(roomMember.userId, getName(roomMember))}
            after={
              <Text size="T200" priority="300" truncate>
                {roomMember.userId}
              </Text>
            }
            before={
              <Avatar size="200">
                <UserAvatar
                  userId={roomMember.userId}
                  src={avatarUrl ?? undefined}
                  alt={getName(roomMember)}
                  renderFallback={() => <Icon size="50" src={Icons.User} filled />}
                />
              </Avatar>
            }
          >
            <Text style={{ flexGrow: 1 }} size="B400" truncate>
              {getName(roomMember)}
            </Text>
          </MenuItem>
        );
      })}
    </AutocompleteMenu>
  );
}
